import { utils, writeFile, WorkBook, WorkSheet } from "xlsx";
import { VendorGroup } from "./VendorCard";
import { VendorLocationData, EventDay } from "./types";
import moment from "moment";

const usd = (cents: number) =>
  `$${(Math.abs(cents) / 100)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

interface Filters {
  eventName?: string;
}

/** Build a worksheet for one vendor (all locations combined) */
const buildVendorSheet = (group: VendorGroup, filters: Filters): WorkSheet => {
  const rows: any[][] = [];
  const push = (...cells: any[]) => rows.push(cells);

  // Get event days from first location
  const eventDays = group.locations[0]?.eventDays ?? [];
  const activeDays = getActiveDays(group.locations, eventDays);

  // Header
  push(filters.eventName || group.locations[0]?.eventName || "");
  push(group.vendorName);
  push("");

  // For each location, output a sales table
  group.locations.forEach((loc, locIdx) => {
    if (group.locations.length > 1) {
      push(`Location: ${loc.locationName}`);
    }

    // Day headers
    const dayHeaderRow: any[] = ["Item"];
    activeDays.forEach((d) => {
      dayHeaderRow.push(d.shortLabel);
      dayHeaderRow.push("");
      dayHeaderRow.push("");
    });
    dayHeaderRow.push("Total");
    dayHeaderRow.push("");
    dayHeaderRow.push("");
    push(...dayHeaderRow);

    // Sub-headers
    const subRow: any[] = [""];
    activeDays.forEach(() => {
      subRow.push("Credit");
      subRow.push("RFID");
      subRow.push("Cash Bal");
    });
    subRow.push("Credit");
    subRow.push("RFID");
    subRow.push("Cash Bal");
    push(...subRow);

    // Item rows
    loc.items.forEach((item) => {
      const row: any[] = [item.itemName];
      activeDays.forEach((d) => {
        const dd = item.byDay[d.date];
        row.push(dd?.credit ? usd(dd.credit) : "-");
        row.push(dd?.rfid ? usd(dd.rfid) : "-");
        row.push(dd?.cashBalance ? usd(dd.cashBalance) : "-");
      });
      row.push(usd(item.totalCredit));
      row.push(usd(item.totalRfid));
      row.push(usd(item.totalCashBalance));
      push(...row);
    });

    // Totals row
    const totalsRow: any[] = ["Totals"];
    activeDays.forEach((d) => {
      const dt = loc.dayTotals[d.date];
      totalsRow.push(dt?.credit ? usd(dt.credit) : "-");
      totalsRow.push(dt?.rfid ? usd(dt.rfid) : "-");
      totalsRow.push(dt?.cashBalance ? usd(dt.cashBalance) : "-");
    });
    const totalCredit = Object.values(loc.dayTotals).reduce((s, d) => s + d.credit, 0);
    const totalRfid = Object.values(loc.dayTotals).reduce((s, d) => s + d.rfid, 0);
    const totalCashBal = Object.values(loc.dayTotals).reduce((s, d) => s + d.cashBalance, 0);
    totalsRow.push(usd(totalCredit));
    totalsRow.push(usd(totalRfid));
    totalsRow.push(usd(totalCashBal));
    push(...totalsRow);

    // Tips row
    const tipsRow: any[] = ["Tips"];
    activeDays.forEach((d) => {
      const dt = loc.dayTotals[d.date];
      tipsRow.push(dt?.tips ? usd(dt.tips) : "-");
      tipsRow.push("");
      tipsRow.push("");
    });
    tipsRow.push(usd(loc.tips));
    push(...tipsRow);

    push("");

    // Financial summary for this location
    push("Gross Sales", usd(loc.grossSales));
    if (loc.grossCredit > 0) push("  Credit Sales", usd(loc.grossCredit));
    if (loc.grossRfid > 0) push("  RFID Sales", usd(loc.grossRfid));
    if (loc.grossCashBalance > 0) push("  Cash Balance Sales", usd(loc.grossCashBalance));
    push("Less Sales Tax", usd(loc.salesTax));
    push("Vendor Net Sales", usd(loc.netSales));
    push(`Commission (${loc.revenueShare}%)`, usd(loc.commissionAmount));
    push("Vendor Commission", usd(loc.vendorCommission));

    if (locIdx < group.locations.length - 1) {
      push("");
      push("────────────────────────────");
      push("");
    }
  });

  // Vendor-level sections
  push("");
  push("═══════════════════════════════");
  push("");

  // Deductions (commissary + manual)
  if (group.deductionCategories.length > 0 || group.manualDeductions.length > 0) {
    push("Deductions");
    group.deductionCategories.forEach((cat) => {
      push(`  ${cat.categoryName} (${cat.totalCount} items)`, usd(cat.totalAmount));
    });
    if (group.deductionCategories.length > 0 && group.manualDeductions.length > 0) {
      push("  Commissary subtotal", usd(group.commissaryTotal));
    }
    group.manualDeductions.forEach((d) => {
      const date = d.created_at ? moment(d.created_at).format("M/D") : "";
      push(`  ${d.description || "Deduction"} (${date})`, usd(d.amount));
    });
    push("Total Deductions", usd(group.totalDeductions));
    push("");
  }

  // Adjustments (additions to payout)
  if (group.adjustments.length > 0) {
    push("Adjustments");
    group.adjustments.forEach((adj) => {
      const date = adj.created_at
        ? moment(adj.created_at).format("M/D")
        : "";
      push(`  ${adj.description || "Adjustment"} (${date})`, usd(adj.amount));
    });
    push("Total Adjustments", usd(group.totalAdjustments));
    push("");
  }

  // Participation Fee
  if (group.participationFee > 0) {
    push("Participation Fee", usd(group.participationFee));
    push("");
  }

  // Payout
  push("Vendor Commission", usd(group.totalVendorCommission));
  if (group.totalTips > 0) push("Tips", usd(group.totalTips));
  if (group.salesTaxResponsible && group.totalTax > 0) push("Sales Tax (vendor remits)", usd(group.totalTax));
  if (group.totalAdjustments > 0) push("Adjustments", usd(group.totalAdjustments));
  if (group.totalDeductions > 0) push("Less: Deductions", `(${usd(group.totalDeductions)})`);
  if (group.participationFee > 0) push("Less: Participation Fee", `(${usd(group.participationFee)})`);
  push("Total Due Vendor", usd(group.totalDue));

  push("");
  push("Cheque payable to:");
  push(group.vendorName);
  push("");
  push("", "Received", "", "________________________");
  push("");
  push("", "Date", "", "________________________");

  // Build worksheet
  const ws = utils.aoa_to_sheet(rows);
  ws["!cols"] = [
    { wch: 28 },
    { wch: 14 },
    ...activeDays.flatMap(() => [{ wch: 12 }, { wch: 12 }, { wch: 12 }]),
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
  ];

  return ws;
};

/** Get days that have any sales across all locations */
const getActiveDays = (locations: VendorLocationData[], eventDays: EventDay[]): EventDay[] => {
  return eventDays.filter((day) =>
    locations.some((loc) => {
      const dt = loc.dayTotals[day.date];
      if (dt && (dt.credit > 0 || dt.rfid > 0 || dt.cashBalance > 0 || dt.tips > 0)) return true;
      return loc.items.some((item) => {
        const d = item.byDay[day.date];
        return d && (d.credit > 0 || d.rfid > 0 || d.cashBalance > 0);
      });
    })
  );
};

export const exportPayoutExcel = (vendorGroups: VendorGroup[], filters: Filters) => {
  const wb: WorkBook = utils.book_new();

  vendorGroups.forEach((group) => {
    const sheetName = (group.vendorName ?? "Vendor")
      .replace(/[\\/*?:\[\]]/g, "")
      .substring(0, 31);
    const ws = buildVendorSheet(group, filters);
    utils.book_append_sheet(wb, ws, sheetName);
  });

  if (vendorGroups.length === 0) {
    const ws = utils.aoa_to_sheet([["No data available"]]);
    utils.book_append_sheet(wb, ws, "Report");
  }

  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
  writeFile(wb, `Vendor_Payouts_${timestamp}.xlsx`);
};
